
import { useState, useEffect } from 'react';
import Button from '../base/Button';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    saveCookiePreferences(allAccepted);
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    setPreferences(necessaryOnly);
    saveCookiePreferences(necessaryOnly);
    setShowBanner(false);
  };

  const saveCustomPreferences = () => {
    saveCookiePreferences(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const saveCookiePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      timestamp: new Date().toISOString(),
      preferences: prefs
    }));

    // Hier können Sie die Cookie-Einstellungen implementieren
    if (prefs.analytics) {
      // Analytics-Cookies aktivieren (z.B. Google Analytics)
      console.log('Analytics-Cookies aktiviert');
    }
    if (prefs.marketing) {
      // Marketing-Cookies aktivieren
      console.log('Marketing-Cookies aktiviert');
    }
  };

  const handlePreferenceChange = (type: keyof CookiePreferences) => {
    if (type === 'necessary') return; // Notwendige Cookies können nicht deaktiviert werden
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl z-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                🍪 Wir verwenden Cookies
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Wir verwenden Cookies und ähnliche Technologien, um Ihnen die bestmögliche Nutzererfahrung zu bieten. 
                Einige Cookies sind für den Betrieb der Website erforderlich, während andere uns helfen, 
                diese Website und Ihre Erfahrung zu verbessern. Sie können Ihre Einstellungen jederzeit anpassen.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 whitespace-nowrap">
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg transition-colors cursor-pointer"
              >
                Einstellungen
              </button>
              <Button
                onClick={acceptNecessary}
                variant="outline"
                size="sm"
                className="whitespace-nowrap"
              >
                Nur notwendige
              </Button>
              <Button
                onClick={acceptAll}
                size="sm"
                className="whitespace-nowrap"
              >
                Alle akzeptieren
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Cookie-Einstellungen</h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                <p className="text-gray-700">
                  Wir verwenden verschiedene Arten von Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                  Sie können auswählen, welche Kategorien Sie zulassen möchten.
                </p>

                {/* Notwendige Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Notwendige Cookies</h3>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={preferences.necessary}
                        disabled
                        className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="ml-2 text-sm text-gray-500">Immer aktiv</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Diese Cookies sind für das ordnungsgemäße Funktionieren der Website erforderlich und können nicht deaktiviert werden. 
                    Sie speichern keine persönlich identifizierbaren Informationen.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Analyse-Cookies</h3>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => handlePreferenceChange('analytics')}
                        className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                      />
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, 
                    indem sie Informationen anonym sammeln und melden. Dies hilft uns, unsere Website zu verbessern.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Marketing-Cookies</h3>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => handlePreferenceChange('marketing')}
                        className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                      />
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Diese Cookies werden verwendet, um Ihnen relevante Werbung zu zeigen. 
                    Sie können auch verwendet werden, um die Anzahl der Anzeigen zu begrenzen und die Effektivität von Werbekampagnen zu messen.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Ihre Rechte</h4>
                  <p className="text-sm text-blue-800">
                    Sie haben das Recht, Ihre Einwilligung jederzeit zu widerrufen. 
                    Weitere Informationen finden Sie in unserer Datenschutzerklärung.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-gray-200">
                <Button
                  onClick={acceptNecessary}
                  variant="outline"
                  className="flex-1 whitespace-nowrap"
                >
                  Nur notwendige Cookies
                </Button>
                <Button
                  onClick={saveCustomPreferences}
                  className="flex-1 whitespace-nowrap"
                >
                  Auswahl speichern
                </Button>
                <Button
                  onClick={acceptAll}
                  className="flex-1 whitespace-nowrap"
                >
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
